// Contents sheet — phones only (CSS hides it from 1000px, where the
// left-rail Contents list from toc.js takes over).
//
// A slim card docked at the bottom shows the section you're in and
// one progress segment per section. It hides while you scroll down,
// comes back when you scroll up or reach the end. Tapping it opens a
// sheet listing every section with its reading time, ticks for the
// ones you've read and a "You're here" line on the current one.
//
// Only activates when:
//   - a [data-post-toc] target exists (post and guide templates)
//   - the post isn't a Testimonial (same rule as toc.js)
//   - .gg-prose has at least MIN_SECTIONS h2s with ids; shorter
//     pieces read fine without it

const MIN_SECTIONS = 4;
const WORDS_PER_MIN = 230;
const HEADING_OFFSET = 80; // matches .gg-prose h2 scroll-margin-top

const pad = (n) => String(n).padStart(2, "0");
const words = (el) => (el.textContent.trim().match(/\S+/g) || []).length;

export default function contentsSheet() {
    if (!document.querySelector("[data-post-toc]")) return;
    if (document.querySelector(".gg-post.tag-testimonial")) return;

    const prose = document.querySelector(".gg-prose");
    if (!prose) return;
    const heads = [...prose.querySelectorAll(":scope > h2[id]")];
    if (heads.length < MIN_SECTIONS) return;

    // Reading time per section: the h2 and everything up to the next h2
    const mins = heads.map((h) => {
        let n = words(h);
        for (let el = h.nextElementSibling; el && el.tagName !== "H2"; el = el.nextElementSibling) {
            n += words(el);
        }
        return Math.max(1, Math.round(n / WORDS_PER_MIN));
    });

    const title = document.querySelector(".gg-post__title");

    const root = document.createElement("div");
    root.className = "gg-contents";
    root.innerHTML = `
        <button class="gg-contents__bar" type="button" aria-haspopup="dialog" aria-label="Open contents">
            <span class="gg-contents__count"><span class="gg-contents__cur"></span> / ${pad(heads.length)}</span>
            <span class="gg-contents__bar-title"></span>
            <span class="gg-contents__open" aria-hidden="true"><i></i><i></i><i></i></span>
            <span class="gg-contents__segs" aria-hidden="true">${heads.map(() => "<span><b></b></span>").join("")}</span>
        </button>
        <div class="gg-contents__scrim"></div>
        <div class="gg-contents__sheet" role="dialog" aria-modal="true" aria-label="Contents">
            <div class="gg-contents__top">
                <button class="gg-contents__grab" type="button" aria-label="Close contents"></button>
                <div class="gg-contents__row">
                    <p class="gg-contents__label">Contents &middot; ${heads.length} sections</p>
                    <button class="gg-contents__close" type="button" aria-label="Close contents"></button>
                </div>
                <p class="gg-contents__title"></p>
                <div class="gg-contents__progress">
                    <span class="gg-contents__pct"></span>
                    <span class="gg-contents__track"><span></span></span>
                    <span class="gg-contents__left"></span>
                </div>
            </div>
            <ol class="gg-contents__list">${heads
                .map(
                    (h, i) => `<li><a href="#${h.id}" data-i="${i}">
                        <span class="gg-contents__num">${pad(i + 1)}</span>
                        <span class="gg-contents__name"></span>
                        <span class="gg-contents__min">${mins[i]} min</span>
                        <span class="gg-contents__here">You're here<span><b></b></span></span>
                    </a></li>`
                )
                .join("")}</ol>
            <div class="gg-contents__foot">
                <button class="gg-contents__btn" type="button" data-contents-top>Back to top</button>
                <button class="gg-contents__btn gg-contents__btn--primary" type="button" data-contents-close>Keep reading</button>
            </div>
        </div>`;

    // Heading text goes in as text, never as markup
    root.querySelector(".gg-contents__title").textContent = title ? title.textContent.trim() : "";
    root.querySelectorAll(".gg-contents__name").forEach((n, i) => {
        n.textContent = heads[i].textContent.trim();
    });
    document.body.appendChild(root);

    const bar = root.querySelector(".gg-contents__bar");
    const sheet = root.querySelector(".gg-contents__sheet");
    const links = [...root.querySelectorAll(".gg-contents__list a")];
    const segs = [...root.querySelectorAll(".gg-contents__segs > span")];
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

    // Open / close
    const open = () => {
        root.classList.add("is-open");
        document.documentElement.classList.add("gg-contents-lock");
        root.querySelector(".gg-contents__close").focus();
        links.find((a) => a.classList.contains("is-on"))?.scrollIntoView({ block: "nearest" });
    };
    const close = (refocus = true) => {
        if (!root.classList.contains("is-open")) return;
        root.classList.remove("is-open");
        document.documentElement.classList.remove("gg-contents-lock");
        if (refocus) bar.focus({ preventScroll: true });
    };
    bar.addEventListener("click", open);
    root.querySelectorAll(".gg-contents__scrim, .gg-contents__grab, .gg-contents__close, [data-contents-close]").forEach((el) =>
        el.addEventListener("click", () => close())
    );
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") close();
    });

    const jump = (top) => {
        close(false);
        window.scrollTo({ top, behavior: reduce.matches ? "auto" : "smooth" });
    };
    root.querySelector("[data-contents-top]").addEventListener("click", () => jump(0));
    links.forEach((a) =>
        a.addEventListener("click", (e) => {
            e.preventDefault();
            const h = heads[+a.dataset.i];
            jump(h.getBoundingClientRect().top + window.scrollY - HEADING_OFFSET);
            history.replaceState(null, "", "#" + h.id);
        })
    );

    // Scroll: which section, how far, and whether the bar shows
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
        ticking = false;
        const y = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const atEnd = y >= max - 40;
        const started = prose.getBoundingClientRect().top < 0;

        if (atEnd || y < lastY - 6) root.classList.remove("is-down");
        else if (y > lastY + 6) root.classList.add("is-down");
        if (Math.abs(y - lastY) > 6 || atEnd) lastY = y;
        root.classList.toggle("is-past", started);

        const line = window.innerHeight * 0.3;
        let cur = 0;
        heads.forEach((h, i) => {
            if (h.getBoundingClientRect().top <= line) cur = i;
        });
        const start = heads[cur].getBoundingClientRect().top;
        const next = heads[cur + 1];
        const end = next ? next.getBoundingClientRect().top : prose.getBoundingClientRect().bottom;
        const within = Math.max(0, Math.min(1, (line - start) / Math.max(1, end - start)));

        let left = mins[cur] * (1 - within);
        for (let i = cur + 1; i < mins.length; i++) left += mins[i];
        const pct = max > 0 ? Math.min(100, Math.round((y / max) * 100)) : 0;

        root.querySelector(".gg-contents__cur").textContent = pad(cur + 1);
        root.querySelector(".gg-contents__bar-title").textContent = heads[cur].textContent.trim();
        root.querySelector(".gg-contents__pct").textContent = pct + "%";
        root.querySelector(".gg-contents__track > span").style.width = pct + "%";
        root.querySelector(".gg-contents__left").textContent = Math.ceil(left) + " min left";

        segs.forEach((s, i) => {
            s.classList.toggle("is-done", i < cur);
            s.firstElementChild.style.width = i === cur ? Math.round(within * 100) + "%" : "";
        });
        links.forEach((a, i) => {
            a.classList.toggle("is-on", i === cur);
            a.classList.toggle("is-read", i < cur);
            if (i === cur) a.querySelector(".gg-contents__here b").style.width = Math.round(within * 100) + "%";
        });
    };

    window.addEventListener(
        "scroll",
        () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(update);
            }
        },
        { passive: true }
    );
    window.addEventListener("resize", update);
    update();
}
